import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { useApi, useWorkspace } from '~/contexts';
import { colors, weights } from '~/styles';

const Container = styled.div`
  min-width: 14rem;
  padding: 0.75rem 0;
`;

const Info = styled.div`
  display: flex;
  margin-top: 0.75rem;

  &:first-child {
    margin-top: 0;
  }
`;

const Label = styled.div`
  width: 10rem;
  padding-right: 0.5rem;
  color: ${colors.grey40};
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  flex-shrink: 0;
  white-space: wrap;
`;

const Value = styled.div`
  white-space: wrap;
`;

const Arrow = styled.div`
  &,
  &::before {
    position: absolute;
    width: 8px;
    height: 8px;
    background: ${colors.white};
  }

  visibility: hidden;

  &::before {
    visibility: visible;
    content: '';
    transform: rotate(45deg);
  }

  bottom: -4px;
`;

const ClientDetails = styled.div`
  max-width: 21rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  border-radius: 0.3125rem;
  background-color: ${colors.white};
  border: solid 1px ${colors.grey10};
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
  z-index: 300;
`;

function ClientPopover({ clientId, placement = 'top-start', ...props }) {
  const [visible, setVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const hovering = useRef(false);
  const forceVisible = useRef(false);

  const modifiers = [
    { name: 'offset', options: { offset: [-24, 12] } },
    { name: 'arrow', options: { element: arrowElement } },
  ];

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers,
    placement,
    strategy: 'fixed',
  });

  const { workspace } = useWorkspace();
  const [query, setQuery] = useState({ isReady: false, data: null });
  const api = useApi();

  useEffect(() => {
    if (!clientId || !visible || query.isReady) return;

    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).allocations().getClientDetails(clientId);
        setQuery({ isReady: true, data });
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, clientId, visible, query.isReady]);

  const handleMouseEnter = () => {
    hovering.current = true;
    // If the query has been executed, delay showing the popover.
    // Otherwise, the delay will be caused by the API query.
    const delay = query.isReady ? 100 : 0;

    setTimeout(() => {
      if (!hovering.current) return;
      setVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    hovering.current = false;

    setTimeout(() => {
      if (!forceVisible.current) setVisible(false);
    }, 200);
  };

  const handleCardMouseEnter = () => {
    forceVisible.current = true;
  };

  const handleCardMouseLeave = () => {
    forceVisible.current = false;
    setVisible(false);
  };

  const client = query.data;

  return (
    <>
      <span ref={setReferenceElement} {...props} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
      {visible &&
        query.isReady &&
        ReactDOM.createPortal(
          <ClientDetails
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={handleCardMouseEnter}
            onMouseLeave={handleCardMouseLeave}>
            <Container>
              <Info>
                <Label>Client Name</Label>
                <Value>{client.name}</Value>
              </Info>

              {client.owner && (
                <Info>
                  <Label>Client Owner</Label>
                  <Value>{client.owner?.name}</Value>
                </Info>
              )}

              {client.practice && (
                <Info>
                  <Label>Client Practice</Label>
                  <Value>{client.practice?.name}</Value>
                </Info>
              )}

              {client.location && (
                <Info>
                  <Label>Client Location</Label>
                  <Value>{client.location?.name}</Value>
                </Info>
              )}

              {client.tags.length > 0 && (
                <Info>
                  <Label>Tags</Label>
                  <Value>{client.tags.map((tag) => tag.name).join(', ')}</Value>
                </Info>
              )}
            </Container>
            <Arrow ref={setArrowElement} style={styles.arrow} />
          </ClientDetails>,
          document.body,
        )}
    </>
  );
}

export default ClientPopover;
